import React, { useState, useRef, useEffect, forwardRef } from "react";
import {Button,
        Dialog,
        DialogActions,
        DialogContent,
        DialogContentText,
        DialogTitle,
        CircularProgress,
        TextField,
        FormControlLabel,
        FormHelperText,
        InputAdornment,
        Grid,
        Box,
        Typography,
        Switch
} from "@material-ui/core";

// localStorage
import { useLocalStorage } from 'usehooks-ts'
import Icon from '@material-ui/core/Icon';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';

import enNZ from 'date-fns/locale/en-NZ'
import { addMonths }  from 'date-fns'
import DatePicker, { registerLocale } from "react-datepicker";

import PageTitle from "../../components/PageTitle";
import Widget from "../../components/Widget";
import { sendNotification } from "../../context/UserContext";
import useStyles from './styles';
import ttAPI from '../../api/ttAPI';
import config from '../../config';
import classnames from "classnames";
import "react-datepicker/dist/react-datepicker.css";

registerLocale('en-NZ', enNZ);

const CPI = forwardRef((props, ref) => {
    const { closeMenu, isPopup } = props;
    const classes = useStyles();
    const [openConfirmCPIDialog, setOpenConfirmCPIDialog] = useState(!isPopup ?? true);
    const [cpi, setCpi] = useState({id:0, quarter:null, percentage:null})
    const [isCreate, setIsCreate] = useState(false);
    const [selectedQuarter, setSelectedQuarter] = React.useState();
    const [currentQuarter, setCurrentQuarter] = React.useState();
    const [nextQuarter, setNextQuarter] = React.useState();
    const quarterRef = useRef(null);
    const quarterCalendarRef = useRef(null);
    var currentQuarterValue = null;
    const [quarterFocused, setQuarterFocused] = React.useState(false);

    const [isCPIValid, setIsCPIValid] = useState(false);
    const [isUpdatingCPI, setIsUpdatingCPI] = useState(false);

    const prevIsCreateRef = useRef();

    const handleClickCustomerConfirmationPopup = () => {
      closeMenu();
      setOpenConfirmCPIDialog(true);
    };

    const handleCloseConfirmationCPI = () => {
      setOpenConfirmCPIDialog(false);
    };

    const handleConfirmCPI = async() => {
        handleCloseConfirmationCPI();
    };

    useEffect(() => {
      if (quarterCalendarRef !== undefined && quarterCalendarRef !== null &&
        quarterCalendarRef.current !== undefined && quarterCalendarRef.current !== null) {
        quarterCalendarRef.current.setOpen(quarterFocused);
      }
    },[quarterFocused]);

    useEffect(() => {
        let mounted = true;
        async function fetchData() {
            const response = await ttAPI.getCPI();
            if(mounted){
                setCpi(response);
            }
        }

        fetchData();

        return () => mounted = false;
    },[]);

    useEffect(() => {
        if (cpi.quarter !== undefined && cpi.quarter !== null && cpi.quarter !== '') {
              var d1 = new Date(cpi.quarter);
              var d2 = new Date(d1);
              d2 = addMonths(d2, 3);
              setSelectedQuarter(d1);
              if (currentQuarter == null) {
                  setCurrentQuarter(d1);
                  setNextQuarter(d2);
              }

        }
    },[cpi]);

    useEffect(() => {
        if (prevIsCreateRef.current !== undefined && prevIsCreateRef.current === !isCreate) {
          if (currentQuarter !== undefined && currentQuarter !== null &&
              nextQuarter !== undefined && nextQuarter !== null) {
              let [cday, cmonth, cyear] = currentQuarter.toLocaleDateString('en-NZ').split("/");
              let [nday, nmonth, nyear] = nextQuarter.toLocaleDateString('en-NZ').split("/");
              setCpi({
                ...cpi,
                quarter: isCreate ? `${nyear}-${nmonth}-${nday}` : `${cyear}-${cmonth}-${cday}`
              });
          }
        }
        prevIsCreateRef.current = isCreate;
    },[isCreate]);

    // triggered if value changes, but not save anywhere as the value here can be in-progress (if use keyboard)
    function handleChangeQuarter(d) {
      currentQuarterValue = d;
    }

    function focusQuarter(event) {
      setQuarterFocused(true);
    }

    function blurQuarter(event) {
      setQuarterFocused(false);
      saveQuarter();
    }

    // triggered if use keyboard
    function handleQuarterKeyPress(event) {
      if (event.which === 9 || event.which === 13 || event.which === 27) {
        setQuarterFocused(false);
        saveQuarter();
      }
    }

    // triggered if select a date
    function handleSelectQuarter(d) {
      saveQuarter();
    }

    function saveQuarter() {
      var d = currentQuarterValue !== undefined && currentQuarterValue !== null ? new Date(currentQuarterValue) : null;
      if (d !== null) {
        let [day, month, year] = d.toLocaleDateString('en-NZ').split("/");
        setCpi({
          ...cpi,
          quarter: `${year}-${month}-${day}`,
        });

        setSelectedQuarter(new Date(currentQuarterValue));
        currentQuarterValue = d;
      }
    }

    function handleChange(e) {
      setCpi({
        ...cpi,
        [e.target.name]: e.target.value,
      });
    }

    async function handleSubmit(e) {
        setIsUpdatingCPI(true);
        var cpiToUse = cpi;
        if (isCreate) {
          cpiToUse.id = 0;
        }
        const response = await ttAPI.saveCPI(cpiToUse);
        if (response !== undefined && response.data !== undefined) {
              if (response.data.success) {
                sendNotification('success', 'Success - CPI Updated.', classes);
              }
              else {
                sendNotification('error', 'Failed to update CPI.', classes);
              }
        }
        setIsUpdatingCPI(false);
    }

    function onClickSwitch() {
        setIsCreate(!isCreate);
    }

    const disableUpdateButton = () => {
        var isCPIValid = cpi !== undefined && cpi !== null &&
                         cpi.quarter !== undefined && cpi.quarter !== null && cpi.quarter !== '' &&
                         cpi.percentage !== undefined && cpi.percentage !== null;
        return isUpdatingCPI || !isCPIValid;
    }

    const switchField = () => {
        return (<>
            <Box display={"flex"} alignItems={"center"}>
                <Typography weight={"medium"}>
                    {isCreate ? 'CREATE' : 'UPDATE'} CPI
                </Typography>
                <Switch color={"primary"} checked={isCreate}
                        onClick={onClickSwitch} />
            </Box>
        </>);
    };

    const quarterField = () => {
        return (<>{cpi &&
          <TextField
          id="quarter"
          ref={quarterRef}
          label="Quarter"
          required
          disabled
          className={classnames({
          'textField': true,
          'calenderField': true,
          [classes.calendarFocused]: quarterFocused,
          [classes.error]: cpi.quarter === null
        })}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon />
                </InputAdornment>
              ),
              inputComponent: () =>
              <DatePicker showMonthYearPicker disabled
              ref={quarterCalendarRef}
              className={classes.textField}
              selected={selectedQuarter}
              onSelect={handleSelectQuarter}
              onChange={handleChangeQuarter}
              onFocus={focusQuarter}
              onClickOutside={blurQuarter}
              onKeyDown={handleQuarterKeyPress}
              locale="en-NZ"
              dateFormat="MMM-yyyy"
              />
            }}
            />}
              </>);
    };

    const percertageField = () => {
      return (<>{cpi &&
              <TextField
              id="percentage"
              label="Percentage"
              autoComplete='off'
              onChange={handleChange}
              value={cpi.percentage === null ? '' : cpi.percentage || undefined}
              name="percentage"
              variant="outlined"
              type={'number'}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon style={{ fontSize: '1rem' }}>%</Icon>
                  </InputAdornment>
                ),
              }}
              required
            />}
            </>);
    };

    const saveButton = () => {
      return (<>
              <Button onClick={handleSubmit} disabled={disableUpdateButton()}>
              {isUpdatingCPI && <span><CircularProgress size={25} color={'inherit'} /> {isCreate ? 'Creating' : 'Updating'} CPI...</span>}
              {!isUpdatingCPI && <span>{(isPopup ?? false) ? 'OK' : 'SAVE'}</span>}
              </Button>
            </>);
    };

  const dialogBox = () => {
    return (
      <>
      <Dialog open={openConfirmCPIDialog} onClose={handleCloseConfirmationCPI} disableBackdropClick={true}
              fullScreen={!isPopup ?? true}
          PaperProps={{
            style: {
              boxShadow: 'none',
            },
          }}
      >
        <DialogTitle id="form-dialog-title">Edit CPI</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Edit CPI
          </DialogContentText>
          <DialogContentText>
            <FormControlLabel
              control={quarterField()}
              label="Quarter"
            />
            <FormControlLabel
              control={percertageField()}
              label="Percentage"
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmationCPI} className={classes.cancelButton}>
            Cancel
          </Button>
          {saveButton()}
        </DialogActions>
      </Dialog>
      </>
    );
  };

    const showFull = () => {
      return (<>
        <PageTitle title="Update CPI" />
        <Grid container spacing={8}>
          <Grid item xs={12} md={12} lg={12}>
          <Widget
            title=""
            upperTitle
            className={classes.card}
            bodyClass={classes.fullHeightBody}
            disableWidgetMenu
            showOverflow
          >

            <div className={classes.formDisplay}>
              {switchField()}
              {quarterField()}
              {percertageField()}
              {saveButton()}
            </div>
            <div></div>
            </Widget>
            </Grid>
          </Grid>
        </>);

    };

    return (isPopup ?? false) ? dialogBox() : showFull();
});
export default CPI;
